import type { ReactNode } from 'react';
import clsx from 'clsx';

export interface AppTab {
  id: string;
  label: string;
  icon?: ReactNode;
  disabled?: boolean;
}

interface AppTabsProps {
  tabs: AppTab[];
  activeId: string;
  onChange: (id: string) => void;
  ariaLabel?: string;
  className?: string;
}

/**
 * Module-level tabs per Jira OOM-1256.
 *
 * - 48px tall row, full width, sits directly below the global header.
 * - 24px horizontal padding, 16px gap between tabs, left-aligned.
 * - Active tab gets a 2px bottom border in `--app-primary`.
 * - Smooth color transitions (150-200ms).
 *
 * Tabs are *visual* navigation; the parent owns the active id and content.
 */
export function AppTabs({
  tabs,
  activeId,
  onChange,
  ariaLabel = 'Module tabs',
  className,
}: AppTabsProps): JSX.Element {
  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={clsx('flex items-center gap-4 px-6', className)}
      style={{
        height: 'var(--app-tabs-h)',
        backgroundColor: 'var(--app-surface)',
        borderBottom: '1px solid var(--app-border)',
      }}
    >
      {tabs.map((tab) => {
        const active = tab.id === activeId;
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={active}
            aria-controls={`tabpanel-${tab.id}`}
            id={`tab-${tab.id}`}
            tabIndex={active ? 0 : -1}
            disabled={tab.disabled}
            onClick={() => !tab.disabled && onChange(tab.id)}
            className={clsx(
              'relative inline-flex h-full items-center gap-2 text-sm font-medium transition-colors',
              'focus-visible:outline-none focus-visible:ring-2',
              'disabled:cursor-not-allowed disabled:opacity-50',
            )}
            style={{
              color: active ? 'var(--app-primary)' : 'var(--app-text-secondary)',
            }}
            onMouseEnter={(e) => {
              if (!active && !tab.disabled) {
                e.currentTarget.style.color = 'var(--app-text-primary)';
              }
            }}
            onMouseLeave={(e) => {
              if (!active) {
                e.currentTarget.style.color = 'var(--app-text-secondary)';
              }
            }}
          >
            {tab.icon && <span aria-hidden>{tab.icon}</span>}
            <span>{tab.label}</span>
            <span
              aria-hidden
              className="absolute inset-x-0 bottom-0 transition-opacity"
              style={{
                height: 2,
                backgroundColor: 'var(--app-primary)',
                opacity: active ? 1 : 0,
              }}
            />
          </button>
        );
      })}
    </div>
  );
}
